import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Coins, Menu, X, Settings2, Globe, ArrowRight, LogOut, UserRound } from "lucide-react";
import Logo from "./Logo";
import { NAV_ITEMS, getCredits, getConfig } from "../mock/mock";
import { useAuth } from "../lib/auth";

const Dropdown = ({ label, items, onGo }) => (
  <div className="relative group">
    <button className="flex items-center gap-1 px-3 py-2 text-[14.5px] font-semibold text-[#463D33] hover:text-[#F26F21] transition-colors">
      {label}
      <ChevronDown size={14} className="mt-[1px] transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
      <div className="w-72 rounded-2xl border border-[#F0E7DD] bg-white shadow-xl shadow-[#F26F21]/5 p-2">
        {items.map((it) => (
          <button
            key={it.label}
            onClick={onGo}
            className="w-full text-left px-4 py-2.5 rounded-xl hover:bg-[#FFF3E9] transition-colors"
          >
            <div className="text-[13.5px] font-bold text-[#241E18]">{it.label}</div>
            <div className="text-[12.5px] text-[#8A7F73]">{it.desc}</div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

const Header = () => {
  const { user, logout } = useAuth();
  const [credits, setCreditsState] = useState(getCredits());
  const [config, setConfigState] = useState(getConfig());
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const updCredits = () => setCreditsState(getCredits());
    const updConfig = () => setConfigState(getConfig());
    window.addEventListener("naturblur-credits", updCredits);
    window.addEventListener("naturblur-config", updConfig);
    return () => {
      window.removeEventListener("naturblur-credits", updCredits);
      window.removeEventListener("naturblur-config", updConfig);
    };
  }, []);

  const visibleNav = NAV_ITEMS.filter((item) => config.menus[item.id]);

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#F0E7DD]">
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-6">
        {/* Logo + nav a la izquierda (distribución propia) */}
        <Link to="/" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 flex-1">
          {visibleNav.map((item) =>
            item.dropdown ? (
              <Dropdown key={item.id} label={item.label} items={item.dropdown} onGo={() => navigate("/editor")} />
            ) : (
              <Link
                key={item.id}
                to={item.to}
                className="px-3 py-2 text-[14.5px] font-semibold text-[#463D33] hover:text-[#F26F21] transition-colors"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        {/* Acciones a la derecha */}
        <div className="flex items-center gap-2 ml-auto">
          {config.header.idioma && (
            <button className="hidden md:flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#EFE4D8] text-[13px] font-bold text-[#463D33] hover:border-[#F26F21] transition-colors">
              <Globe size={14} className="text-[#F26F21]" />
              ES
            </button>
          )}
          {config.header.creditos && (
            <Link
              to="/tarifas"
              title="Tus créditos"
              className="flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#FFF3E9] border border-[#FBD9BC] text-[#C2570F] font-extrabold text-[13.5px] hover:bg-[#FDEBDC] transition-colors"
            >
              <Coins size={14} />
              {credits}
            </Link>
          )}
          {config.header.ctaEditor && (
            <Link
              to="/editor"
              className="hidden sm:inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#F26F21] text-white text-[14px] font-bold hover:bg-[#D95B12] hover:shadow-lg hover:shadow-[#F26F21]/25 transition-all"
            >
              Ir al editor
              <ArrowRight size={15} />
            </Link>
          )}
          {user?.role === "admin" && (
            <Link
              to="/admin"
              title="Backoffice"
              className="w-9 h-9 rounded-full border border-[#EFE4D8] flex items-center justify-center text-[#8A7F73] hover:text-[#F26F21] hover:border-[#F26F21] transition-colors"
            >
              <Settings2 size={16} />
            </Link>
          )}
          {user && (
            <button
              data-testid="header-logout-btn"
              onClick={logout}
              title={`Cerrar sesión (${user.email})`}
              className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-[#EFE4D8] text-[12.5px] font-bold text-[#463D33] hover:border-[#F26F21] hover:text-[#F26F21] transition-colors"
            >
              <UserRound size={14} className="text-[#F26F21]" />
              <span className="hidden md:inline max-w-[140px] truncate">{user.name || user.email}</span>
              <LogOut size={13} />
            </button>
          )}
          <button
            className="lg:hidden w-10 h-10 rounded-full border border-[#EFE4D8] flex items-center justify-center text-[#241E18]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menú"
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Menú móvil */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-[#F0E7DD] bg-white px-6 py-4 space-y-1">
          {visibleNav.map((item) => (
            <Link
              key={item.id}
              to={item.to || "/editor"}
              onClick={() => setMobileOpen(false)}
              className="block px-2 py-2.5 rounded-lg text-[15px] font-semibold text-[#463D33] hover:bg-[#FFF3E9] transition-colors"
            >
              {item.label}
            </Link>
          ))}
          {config.header.ctaEditor && (
            <Link
              to="/editor"
              onClick={() => setMobileOpen(false)}
              className="mt-2 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#F26F21] text-white text-[14px] font-bold"
            >
              Ir al editor <ArrowRight size={15} />
            </Link>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
